import { useState, useMemo } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSection, SearchAutocomplete } from '@/shared/ui/components/form';
import type { SearchAutocompleteOption } from '@/shared/ui/components/form';
import { Button } from '@/shared/ui/components/basic';
import { useAgency } from '@/pages/authenticated/agency-pages/AgenciesContext';
import { isSoloBrokerAgency } from '@/shared/utils/agency-type';
import type { PropertyFormValues } from './schema';

interface AssignmentSectionProps {
  form: UseFormReturn<PropertyFormValues>;
}

/**
 * Renders the broker assignment section with searchable selectors.
 * Hidden for solo-broker agencies (single owner, not a multi-broker imobiliária).
 * Primary broker is selected via autocomplete from agency members.
 * Additional brokers are shown as removable chips once selected.
 */
export function AssignmentSection({ form }: AssignmentSectionProps) {
  const { t } = useTranslation('features/properties');
  const [collapsed, setCollapsed] = useState(true);
  const agency = useAgency((s) => s.agency);

  /**
   * Agency members keyed by e-mail — the identity every assignment field
   * persists (`captured_by`, `primary_broker`, `assigned_brokers`), matching
   * what the detail sidebar and the lease service read back.
   */
  const brokerOptions: SearchAutocompleteOption[] = useMemo(() => {
    const members = agency.members ?? [];
    return members.map((m) => ({
      id: m.email,
      label: `${m.first_name} ${m.last_name}`.trim() || m.email,
      sublabel: m.email,
    }));
  }, [agency.members]);

  const assignedBrokers = form.watch('assigned_brokers') ?? [];

  if (isSoloBrokerAgency(agency.type)) {
    return null;
  }

  function handleAddBroker(id: string | null) {
    if (!id) return;
    if (assignedBrokers.includes(id)) return;
    form.setValue('assigned_brokers', [...assignedBrokers, id], { shouldDirty: true });
  }

  function handleRemoveBroker(id: string) {
    form.setValue(
      'assigned_brokers',
      assignedBrokers.filter((b) => b !== id),
      { shouldDirty: true },
    );
  }

  function brokerLabel(id: string): string {
    const opt = brokerOptions.find((o) => o.id === id);
    return opt?.label ?? id;
  }

  return (
    <FormSection
      title={t('propertyDrawer.sections.assignment', 'Assignment')}
      collapsed={collapsed}
      onToggle={() => setCollapsed(prev => !prev)}
    >
      <Controller
        name="captured_by"
        control={form.control}
        render={({ field }) => (
          <SearchAutocomplete
            label={t('propertyDrawer.fields.capturedBy', 'Who sourced the property')}
            options={brokerOptions}
            value={field.value ?? null}
            onChange={(id) => field.onChange(id ?? undefined)}
            placeholder={t('propertyDrawer.fields.capturedByPlaceholder', 'Search member...')}
            emptyLabel={t('propertyDrawer.fields.noBrokersFound', 'No brokers found')}
          />
        )}
      />

      <Controller
        name="primary_broker"
        control={form.control}
        render={({ field }) => (
          <SearchAutocomplete
            label={t('propertyDrawer.fields.primaryBroker', 'Primary broker')}
            options={brokerOptions}
            value={field.value ?? null}
            onChange={(id) => field.onChange(id ?? undefined)}
            placeholder={t('propertyDrawer.fields.primaryBrokerPlaceholder', 'Search broker...')}
            emptyLabel={t('propertyDrawer.fields.noBrokersFound', 'No brokers found')}
          />
        )}
      />

      <div>
        <SearchAutocomplete
          label={t('propertyDrawer.fields.assignedBrokers', 'Assigned brokers')}
          options={brokerOptions.filter((o) => !assignedBrokers.includes(o.id))}
          value={null}
          onChange={handleAddBroker}
          placeholder={t('propertyDrawer.fields.assignedBrokersPlaceholder', 'Add broker...')}
          emptyLabel={t('propertyDrawer.fields.noBrokersFound', 'No brokers found')}
        />
        {assignedBrokers.length > 0 && (
          <div className="flex flex-wrap gap-(--space-2) mt-(--space-2)">
            {assignedBrokers.map((id) => (
              <div
                key={id}
                className="inline-flex items-center gap-(--space-1) px-(--space-2) py-(--space-1) text-[var(--text-xs)] rounded-(--radius-md) bg-[var(--surface-tertiary)] text-[var(--text-primary)]"
              >
                {brokerLabel(id)}
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => handleRemoveBroker(id)}
                  className="text-[var(--text-secondary)] hover:text-[var(--status-error)] ml-(--space-1) p-0 h-auto min-h-0"
                  aria-label={t('common:remove', 'Remove')}
                >
                  ×
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </FormSection>
  );
}
